//src/store/reducers/claims.reducer.ts
import { createSlice, PayloadAction } from '@reduxjs/toolkit'
import {
  addClaim,
  updateClaim,
  removeClaim,
  fetchClaims,
} from '@store/actions/claims.action'
import { addNote, updateNote } from '@store/actions/note.action'
import { INote, NoteContextType } from '@dataTypes/note.type'

interface ClaimsState {
  data: any[]
  loading: boolean
  error: string | null
}

const initialState: ClaimsState = {
  data: [],
  loading: false,
  error: null,
}

const claimsSlice = createSlice({
  name: 'claims',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    const syncClaimNotesFromNoteMutation = (
      state: ClaimsState,
      note: INote,
    ): void => {
      if (note.context_type !== NoteContextType.CLAIMS || !note.context_id) {
        return
      }

      const contextId = String(note.context_id)
      const claimsBucket = Array.isArray(state.data)
        ? state.data
        : Array.isArray((state.data as { claims?: unknown[] })?.claims)
          ? ((state.data as { claims: unknown[] }).claims as Array<
              Record<string, unknown>
            >)
          : []

      const claimIndex = claimsBucket.findIndex((claim) => {
        const claimId = claim.id != null ? String(claim.id) : ''
        const claimContextId =
          claim.claim_id != null ? String(claim.claim_id) : ''
        return claimId === contextId || claimContextId === contextId
      })

      if (claimIndex === -1) {
        return
      }

      const targetClaim = claimsBucket[claimIndex] as {
        notes?: INote[]
      } & Record<string, unknown>
      const existingNotes = targetClaim.notes ?? []
      const noteIndex = existingNotes.findIndex(
        (n) => n.note_id === note.note_id,
      )
      const nextNotes =
        noteIndex === -1
          ? [...existingNotes, note]
          : existingNotes.map((n, idx) => (idx === noteIndex ? note : n))

      claimsBucket[claimIndex] = {
        ...targetClaim,
        notes: nextNotes,
      }
    }

    // builder.addCase(fetchAllClaims.pending, (state) => {
    //   state.loading = true
    //   state.error = null
    // })
    // builder.addCase(fetchAllClaims.fulfilled, (state, action) => {
    //   state.loading = false
    //   state.claims = action.payload
    // })
    // builder.addCase(fetchAllClaims.rejected, (state, action) => {
    //   state.loading = false
    //   state.error = (action.payload as string) || 'Failed to fetch claims'
    // })

    builder.addCase(fetchClaims.pending, (state) => {
      state.loading = true
      state.error = null
    })
    builder.addCase(
      fetchClaims.fulfilled,
      (state, action: PayloadAction<any>) => {
        state.data = action.payload
        state.loading = false
      },
    )
    builder.addCase(fetchClaims.rejected, (state, action) => {
      state.loading = false
      state.error = action.payload as string
    })

    builder.addCase(addClaim.fulfilled, (state, action) => {
      state.loading = false
      state.data.push(action.payload)
    })
    builder.addCase(addClaim.rejected, (state, action) => {
      state.loading = false
      state.error = (action.payload as string) || 'Failed to add claim'
    })

    builder.addCase(updateClaim.fulfilled, (state, action) => {
      state.loading = false
      const { id, updatedClaim } = action.payload
      const index = state.data.findIndex((claim) => claim.id === id)
      if (index !== -1) {
        state.data[index] = {
          ...state.data[index],
          ...updatedClaim,
        }
      }
    })
    builder.addCase(updateClaim.rejected, (state, action) => {
      state.loading = false
      state.error = (action.payload as string) || 'Failed to update claim'
    })

    builder.addCase(removeClaim.fulfilled, (state, action) => {
      state.loading = false
      state.data = state.data.filter((claim) => claim.id !== action.payload)
    })
    builder.addCase(removeClaim.rejected, (state, action) => {
      state.loading = false
      state.error = (action.payload as string) || 'Failed to remove claim'
    })

    builder.addCase(
      addNote.fulfilled,
      (state, action: PayloadAction<INote>) => {
        syncClaimNotesFromNoteMutation(state, action.payload)
      },
    )
    builder.addCase(
      updateNote.fulfilled,
      (state, action: PayloadAction<INote>) => {
        syncClaimNotesFromNoteMutation(state, action.payload)
      },
    )
  },
})

export default claimsSlice.reducer
